import React, { useState, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Package, Zap, ZapOff, Ban, ChevronUp, ChevronDown, ChevronsUpDown, Lock, Edit2, ScanLine, AlertTriangle } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import CourierCostModal from './CourierCostModal'
import OrderDetailDrawer from './tracking/OrderDetailDrawer'

function formatCurrency(v) {
  // Guard non-finite values (NaN/Infinity/undefined arithmetic slip-ups)
  // so the UI shows an em-dash instead of "A$NaN".
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(n)
}

function safeDate(dateStr) {
  if (!dateStr) return '—'
  try {
    return format(parseISO(dateStr), 'dd/MM/yy')
  } catch {
    return dateStr
  }
}

// Whole days since ship date (for the tracking drawer header). null when unknown.
function daysInTransit(shippedDate) {
  if (!shippedDate) return null
  try {
    const days = Math.floor((Date.now() - parseISO(shippedDate).getTime()) / 86400000)
    return days >= 0 ? days : null
  } catch {
    return null
  }
}

const CARRIER_ALIASES = {
  australia_post_mypost_business: 'australia_post',
}

function formatCarrierBadge(carrier) {
  if (!carrier) return '—'
  return CARRIER_ALIASES[carrier] ?? carrier
}

/**
 * Tooltip that uses fixed positioning so it's never clipped by overflow containers.
 */
function Tooltip({ tip, children }) {
  const [pos, setPos] = useState(null)
  const ref = useRef(null)

  if (!tip) return children

  function handleEnter() {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect()
      // getBoundingClientRect() is viewport-relative and the tooltip is
      // position:fixed (also viewport-relative), so do NOT add window.scrollY —
      // adding it drops the tooltip scrollY px too low once the page is scrolled.
      setPos({ top: r.top, left: r.left + r.width / 2 })
    }
  }

  return (
    <span
      ref={ref}
      className="inline-block"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && (
        <span
          style={{ position: 'fixed', top: pos.top - 8, left: pos.left, transform: 'translate(-50%, -100%)' }}
          className="z-[9999] min-w-max rounded-lg bg-slate-800 text-white text-xs px-3 py-2 shadow-lg pointer-events-none leading-relaxed whitespace-pre-line"
        >
          {tip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </span>
      )}
    </span>
  )
}

/**
 * Styled badge for a single ShipStation tag.
 * Known tags get distinctive colours; unknown tags get a neutral grey.
 */
function TagBadge({ name }) {
  const style =
    name === 'Custom Insert' ? 'bg-purple-100 text-purple-700' :
    name === 'WHOLESALE'     ? 'bg-amber-100  text-amber-700'  :
                               'bg-slate-100  text-slate-600'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${style} mr-1 last:mr-0`}>
      {name}
    </span>
  )
}

/**
 * Per-row dropdown for insert type: Standard / Custom / No Insert.
 *
 * Display precedence (mirrors backend pricing/reprice):
 *   1. hasCustomInsert → 'custom' (tag- or user-set)
 *   2. insertOverridden → use the order's flags as the source of truth
 *      (lets the user pick Standard on a default-no-insert client)
 *   3. else derive from the order's effective insert_fee — it already reflects
 *      the scheduled insert period / trigger SKUs / client default, so the
 *      dropdown matches what's actually charged (a 'standard' period on a
 *      default-no-insert client now reads "Standard", not "No Insert").
 */
function InsertTypeSelect({ orderId, hasCustomInsert, noInsert, insertOverridden, insertFee, onChange }) {
  let value
  if (hasCustomInsert) {
    value = 'custom'
  } else if (insertOverridden) {
    value = noInsert ? 'none' : 'standard'
  } else {
    value = Number(insertFee) > 0 ? 'standard' : 'none'
  }
  return (
    <select
      value={value}
      onChange={e => onChange(orderId, e.target.value)}
      onClick={e => e.stopPropagation()}
      className={[
        'text-xs rounded border px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-purple-400 cursor-pointer',
        value === 'custom'   ? 'border-purple-300 bg-purple-50 text-purple-700 font-medium' :
        value === 'none'     ? 'border-slate-300  bg-slate-100 text-slate-400  line-through'  :
                               'border-slate-200  bg-white     text-slate-500',
      ].join(' ')}
    >
      <option value="standard">Standard</option>
      <option value="custom">Custom</option>
      <option value="none">No Insert</option>
    </select>
  )
}

function SkeletonRow({ cols }) {
  return (
    <tr className="border-b border-slate-100">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="skeleton h-4 rounded w-full" />
        </td>
      ))}
    </tr>
  )
}

/**
 * Indeterminate checkbox — sets the DOM indeterminate property via ref.
 */
function IndeterminateCheckbox({ checked, indeterminate, onChange, className }) {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={className}
    />
  )
}

/**
 * Inline case-pick qty input with optional split regular items.
 * Saves on blur or Enter; clears on empty/0.
 * onChange(orderId, cartonsQty, splitItemsQty)
 */
function CasePickInput({ orderId, initialQty, initialSplitItems = 0, onChange }) {
  const [cartonsVal, setCartonsVal] = useState(initialQty > 0 ? String(initialQty) : '')
  const [itemsVal, setItemsVal]     = useState(initialSplitItems > 0 ? String(initialSplitItems) : '')
  const prevCartonsRef = useRef(initialQty)
  const prevItemsRef   = useRef(initialSplitItems)

  // Sync when server refreshes values
  useEffect(() => {
    if (initialQty !== prevCartonsRef.current) {
      prevCartonsRef.current = initialQty
      setCartonsVal(initialQty > 0 ? String(initialQty) : '')
    }
    if (initialSplitItems !== prevItemsRef.current) {
      prevItemsRef.current = initialSplitItems
      setItemsVal(initialSplitItems > 0 ? String(initialSplitItems) : '')
    }
  }, [initialQty, initialSplitItems])

  function commitCartons() {
    const qty = parseInt(cartonsVal, 10)
    const resolvedQty = isNaN(qty) || qty <= 0 ? 0 : qty

    // When carton qty changes, always reset split items to 0 — pure case pick by default.
    // The user must explicitly enter a value in the split items field to enable split mode.
    // This prevents stale split_regular_items values from being carried forward.
    const qtyChanged = resolvedQty !== prevCartonsRef.current
    const resolvedSplit = qtyChanged ? 0 : (resolvedQty > 0 ? (parseInt(itemsVal, 10) || 0) : 0)

    if (resolvedQty !== prevCartonsRef.current || resolvedSplit !== prevItemsRef.current) {
      prevCartonsRef.current = resolvedQty
      prevItemsRef.current   = resolvedSplit
      onChange(orderId, resolvedQty, resolvedSplit)
    }
    setCartonsVal(resolvedQty > 0 ? String(resolvedQty) : '')
    // Clear split items when cartons are cleared or when carton qty changes
    if (resolvedQty <= 0 || qtyChanged) setItemsVal('')
  }

  function commitSplitItems() {
    const cartons = parseInt(cartonsVal, 10) || 0
    if (cartons <= 0) return
    const split = parseInt(itemsVal, 10) || 0
    if (split !== prevItemsRef.current) {
      prevItemsRef.current = split
      onChange(orderId, cartons, split)
    }
  }

  const cartonsQty = parseInt(cartonsVal, 10) || 0

  return (
    <div className="flex flex-col gap-0.5 items-end">
      {/* Carton qty */}
      <input
        type="number"
        min="0"
        value={cartonsVal}
        onChange={e => setCartonsVal(e.target.value)}
        onBlur={commitCartons}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }}
        placeholder="—"
        title="Carton pick quantity"
        className={[
          'w-16 text-right text-xs rounded border px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-amber-400',
          cartonsVal ? 'border-amber-400 bg-amber-50 text-amber-800 font-semibold' : 'border-slate-200 bg-white text-slate-400',
        ].join(' ')}
      />
      {/* Split regular items — only shown when cartons are set */}
      {cartonsQty > 0 && (
        <div className="flex items-center gap-0.5">
          <span className="text-slate-400 text-xs leading-none select-none">+</span>
          <input
            type="number"
            min="0"
            value={itemsVal}
            onChange={e => setItemsVal(e.target.value)}
            onBlur={commitSplitItems}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }}
            placeholder="items"
            title="Regular items to charge alongside carton picks (pick fee + additional items)"
            className={[
              'w-14 text-right text-xs rounded border px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400',
              itemsVal ? 'border-blue-400 bg-blue-50 text-blue-800 font-semibold' : 'border-slate-200 bg-white text-slate-400',
            ].join(' ')}
          />
        </div>
      )}
    </div>
  )
}

/**
 * @param {object}   props
 * @param {Array}    props.orders
 * @param {boolean}  props.loading
 * @param {number}   props.total
 * @param {number}   props.page              — 1-based
 * @param {number}   [props.pageSize]
 * @param {function} props.onPageChange
 * @param {function} [props.onPageSizeChange]
 * @param {boolean}  [props.showCarrierCost]
 * @param {function} [props.onInsertTypeChange]         — (orderId, insertType) per-row insert dropdown
 * @param {function} [props.onCasePickChange]           — (orderId, qty) called on blur; qty=0 clears
 * @param {function} [props.onFlatRateExceptionToggle]  — (orderId, currentValue) toggle flat rate exception
 * @param {function} [props.onWaive]                    — (orderId, currentWaivedState) toggle fee waiver
 */
export default function OrderTable({
  orders = [],
  // A failed fetch leaves `orders` at its [] default, which renders exactly
  // like a genuinely empty result — "No orders found · Try adjusting your
  // filters". That sent us chasing filters while the request was erroring.
  error = null,
  loading = false,
  total = 0,
  page = 1,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  showCarrierCost = false,
  defaultNoInsert = false,
  sort = null,
  onSortChange = null,
  onInsertTypeChange,
  onCasePickChange,
  onFlatRateExceptionToggle,
  onWaive,
  onEditOrder,
  getTrackingEvents,
}) {
  const totalPages = Math.ceil(total / pageSize) || 1

  const showCasePick = !!onCasePickChange
  const showInsertType = !!onInsertTypeChange
  const showWaive = !!onWaive
  const showEdit = !!onEditOrder
  const showTracking = !!getTrackingEvents

  // Per-order courier cost override modal (admin only — gated on showCarrierCost)
  const [costOverrideOrderId, setCostOverrideOrderId] = useState(null)
  // Per-order tracking timeline drawer (opens from the Tracking column)
  const [drawerOrder, setDrawerOrder] = useState(null)

  // cols: date + order# + recipient + carrier + weight + items
  //       + pick + add.item + labelling + insert
  //       + shipping + [carrier cost] + total + tags + [cp_qty] + [insert dropdown] + [waive]
  const baseCols = (showCarrierCost ? 13 : 12) + (showCasePick ? 1 : 0) + (showTracking ? 1 : 0) + 1  // +1 for tags
  const cols = (showInsertType ? baseCols + 1 : baseCols) + (showWaive ? 1 : 0) + (showEdit ? 1 : 0)

  // Sortable column header
  const SortTh = ({ col, label, right = false, color = 'text-slate-500' }) => {
    if (!onSortChange) {
      const cls = `px-2 py-2 ${right ? 'text-right' : 'text-left'} text-xs font-semibold ${color} uppercase tracking-wider`
      return <th className={cls}>{label}</th>
    }
    const active = sort?.col === col
    const asc    = sort?.dir === 'asc'
    const Icon   = active ? (asc ? ChevronUp : ChevronDown) : ChevronsUpDown
    const cls = `px-2 py-2 ${right ? 'text-right' : 'text-left'} text-xs font-semibold uppercase tracking-wider cursor-pointer select-none group whitespace-nowrap`
    return (
      <th className={`${cls} ${active ? color : 'text-slate-400 hover:text-slate-600'}`}
          onClick={() => onSortChange(col)}>
        <span className="inline-flex items-center gap-1">
          {!right && label}
          <Icon size={12} className={active ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'} />
          {right && label}
        </span>
      </th>
    )
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-xs">
          <thead className="bg-slate-50">
            <tr>
              <SortTh col="shipped_date"    label="Date" />
              <SortTh col="order_number"    label="Order #" />
              <SortTh col="recipient_suburb" label="Recipient" />
              <SortTh col="carrier"         label="Carrier" />
              {showTracking && (
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Tracking
                </th>
              )}
              <SortTh col="weight_kg"       label="Wt (kg)"   right />
              <SortTh col="item_count"      label="Qty"       right />
              <SortTh col="pick_fee"        label="Pick"      right />
              <SortTh col="pack_fee"        label="+Items"    right />
              <SortTh col="handling_fee"    label="Label"     right />
              <SortTh col="insert_fee"      label="Insert"    right />
              <SortTh col="shipping_cost"   label="Ship"      right />
              {showCarrierCost && (
                <th className="px-2 py-2 text-right text-xs font-semibold text-amber-600 uppercase tracking-wider">
                  Cost
                </th>
              )}
              <SortTh col="total_charged" label="Total" right color="text-slate-700" />
              <th className="px-2 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tags</th>
              {showCasePick && (
                <th className="px-2 py-2 text-right text-xs font-semibold text-amber-600 uppercase tracking-wider">
                  <Tooltip tip={"Carton Pick Qty\n+ regular items (split order)"}>
                    <span className="cursor-default">CP Split</span>
                  </Tooltip>
                </th>
              )}
              {showInsertType && (
                <th className="px-2 py-2 text-left text-xs font-semibold text-purple-600 uppercase tracking-wider">
                  Insert Type
                </th>
              )}
              {showWaive && (
                <th className="px-2 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Waive
                </th>
              )}
              {showEdit && (
                <th className="px-2 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Edit
                </th>
              )}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-slate-100">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <SkeletonRow key={i} cols={cols} />
                ))
              : error
              ? (
                <tr>
                  <td colSpan={cols} className="py-16 text-center">
                    <AlertTriangle size={32} className="mx-auto text-red-400 mb-2" />
                    <p className="text-slate-600 text-sm font-medium">Couldn&rsquo;t load orders</p>
                    <p className="text-slate-400 text-xs mt-1">
                      This is <b>not</b> an empty result — don&rsquo;t adjust your filters.
                    </p>
                    <p className="text-red-600 text-xs mt-2 font-mono break-all px-6">
                      {error?.response?.data?.detail || error?.message || 'Unknown error'}
                    </p>
                  </td>
                </tr>
              )
              : orders.length === 0
              ? (
                <tr>
                  <td colSpan={cols} className="py-16 text-center">
                    <Package size={32} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">No orders found</p>
                    <p className="text-slate-300 text-xs mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              )
              : orders.map((order) => {
                const deliveryTip = [
                  order.recipient_name,
                  [order.recipient_suburb, order.recipient_state, order.recipient_postcode].filter(Boolean).join(', '),
                  order.tracking_number ? `Tracking: ${order.tracking_number}` : null,
                ].filter(Boolean).join('\n')

                const carrierTip = order.service || null

                // A row whose ShipStation label was voided and replaced is
                // already excluded from the invoice — billing's
                // _drop_dead_voided drops label_voided rows with no scans, and
                // MTD/rollover apply the same rule. Rendering it like a live
                // row made this table read higher than the invoice it explains,
                // which is how a void-and-relabel looked like a double charge.
                //
                // Styled distinctly from waived on purpose: waived is your
                // manual call (red, reversible from the Ban toggle), voided is
                // the system reporting what ShipStation did (inert grey).
                const voided     = !!order.label_voided && !order.waived
                const nonBilling = order.waived || !!order.label_voided

                return (
                  <tr
                    key={order.id}
                    className={`transition-colors ${
                      order.waived ? 'bg-red-50 hover:bg-red-100/60'
                      : voided    ? 'bg-slate-100/70 hover:bg-slate-200/60'
                      : 'hover:bg-slate-50'
                    }`}
                  >
                    {/* Date (ship date) */}
                    <td className="px-2 py-2 text-slate-600 whitespace-nowrap text-xs">
                      {safeDate(order.shipped_date || order.order_date)}
                    </td>

                    {/* Order # */}
                    <td className="px-2 py-2 font-mono font-medium text-slate-800 whitespace-nowrap text-xs">
                      {order.order_number}
                      {voided && (
                        <Tooltip tip={'Label voided in ShipStation and replaced.\nNo tracking scans, so billing, MTD and rollover already exclude this row.'}>
                          <span className="ml-1 inline-flex items-center gap-0.5 align-middle text-[9px] font-semibold bg-slate-200 text-slate-600 rounded px-1 cursor-default">
                            <Ban size={9} /> VOID
                          </span>
                        </Tooltip>
                      )}
                    </td>

                    {/* Recipient — hover shows full delivery address + tracking */}
                    <td className="px-2 py-2 text-slate-600">
                      <Tooltip tip={deliveryTip}>
                        <span className="font-medium text-slate-800 cursor-default text-xs">{order.recipient_suburb}</span>
                        {order.recipient_state && (
                          <span className="text-slate-400 ml-1 text-xs">{order.recipient_state}</span>
                        )}
                      </Tooltip>
                    </td>

                    {/* Carrier — hover shows service name */}
                    <td className="px-2 py-2">
                      <Tooltip tip={carrierTip}>
                        <span className={`badge bg-slate-100 text-slate-600 text-xs ${carrierTip ? 'cursor-default' : ''}`}>
                          {formatCarrierBadge(order.carrier)}
                        </span>
                      </Tooltip>
                    </td>

                    {/* Tracking — truncated number; click opens the full timeline drawer */}
                    {showTracking && (
                      <td className="px-2 py-2 whitespace-nowrap">
                        {order.tracking_number ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setDrawerOrder(order)
                            }}
                            className={`inline-flex items-center gap-1 font-mono text-[11px] ${
                              voided
                                ? 'text-slate-400 line-through hover:text-slate-600'
                                : 'text-blue-600 hover:text-blue-800 hover:underline'
                            }`}
                            title={voided
                              ? `Voided label — never scanned · ${order.tracking_number}`
                              : `View tracking · ${order.tracking_number}`}
                          >
                            <ScanLine size={11} className="flex-shrink-0" />
                            …{order.tracking_number.slice(-8)}
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    )}

                    {/* Weight */}
                    <td className="px-2 py-2 text-right text-slate-600 tabular-nums text-xs">
                      {order.weight_kg != null ? Number(order.weight_kg).toFixed(2) : '—'}
                    </td>

                    {/* Items — total quantity of items in the order (item_count) */}
                    <td className="px-2 py-2 text-right text-slate-600 tabular-nums text-xs">
                      {order.item_count != null ? order.item_count : '—'}
                    </td>

                    {/* Pick fee */}
                    <td className="px-2 py-2 text-right tabular-nums text-xs">
                      <span className={nonBilling ? 'line-through text-slate-400' : 'text-slate-700'}>
                        {formatCurrency(order.pick_fee)}
                      </span>
                    </td>

                    {/* Additional item fee (pack_fee) */}
                    <td className="px-2 py-2 text-right tabular-nums text-xs">
                      <span className={nonBilling ? 'line-through text-slate-400' : 'text-slate-700'}>
                        {formatCurrency(order.pack_fee)}
                      </span>
                    </td>

                    {/* Labelling fee (handling_fee) */}
                    <td className="px-2 py-2 text-right tabular-nums text-xs">
                      <span className={nonBilling ? 'line-through text-slate-400' : 'text-slate-700'}>
                        {formatCurrency(order.handling_fee)}
                      </span>
                    </td>

                    {/* Insert fee */}
                    <td className="px-2 py-2 text-right tabular-nums text-xs">
                      {nonBilling ? (
                        <span className="line-through text-slate-400">{formatCurrency(order.insert_fee)}</span>
                      ) : order.no_insert ? (
                        <span className="text-slate-300">—</span>
                      ) : order.has_custom_insert ? (
                        <span className="text-purple-700 font-medium">
                          {formatCurrency(order.insert_fee)}
                          <span className="ml-1 text-xs font-semibold bg-purple-100 text-purple-600 rounded px-1">C</span>
                        </span>
                      ) : (
                        <span className="text-slate-700">{formatCurrency(order.insert_fee)}</span>
                      )}
                    </td>

                    {/* Shipping */}
                    <td className="px-2 py-2 text-right tabular-nums text-xs">
                      <div className="flex items-center justify-end gap-1">
                        {!nonBilling && onFlatRateExceptionToggle && (
                          <button
                            onClick={e => { e.stopPropagation(); onFlatRateExceptionToggle(order.id, order.flat_rate_exception) }}
                            title={order.flat_rate_exception ? 'Exception: charging full carrier rate — click to revert to flat rate' : 'Click to charge full carrier rate for this order'}
                            className={`p-0.5 rounded transition-colors ${order.flat_rate_exception ? 'text-amber-500 hover:text-amber-700' : 'text-slate-200 hover:text-slate-400'}`}
                          >
                            {order.flat_rate_exception ? <ZapOff size={12} /> : <Zap size={12} />}
                          </button>
                        )}
                        <span className={
                          nonBilling ? 'line-through text-slate-400' :
                          order.flat_rate_exception ? 'text-amber-700 font-medium' : 'text-slate-700'
                        }>
                          {formatCurrency(order.shipping_cost)}
                        </span>
                      </div>
                    </td>

                    {/* Carrier cost (admin only) — clickable to open override modal.
                        Displays effective_courier_cost when present (FedEx-adjusted
                        or manually overridden), falling back to raw carrier_cost. */}
                    {showCarrierCost && (() => {
                      const effective = order.effective_courier_cost
                      const display = effective != null ? effective : order.carrier_cost
                      const isLocked = !!order.effective_courier_cost_manually_set
                      const isAdjusted = effective != null && effective !== order.carrier_cost
                      return (
                        <td className="px-2 py-2 text-right font-medium tabular-nums text-xs">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setCostOverrideOrderId(order.id)
                            }}
                            className="inline-flex items-center gap-1 hover:bg-amber-50 rounded px-1 py-0.5 transition-colors group"
                            title={
                              isLocked ? 'Manually locked. Click to view / edit / clear.'
                              : isAdjusted ? 'Adjusted by FedEx reconciliation. Click to override.'
                              : 'Click to override the courier cost.'
                            }
                          >
                            {isLocked && <Lock size={10} className="text-purple-500 flex-shrink-0" />}
                            <span className={isAdjusted && !isLocked ? 'text-blue-700' : 'text-amber-700'}>
                              {formatCurrency(display)}
                            </span>
                          </button>
                        </td>
                      )
                    })()}

                    {/* Total */}
                    <td className="px-2 py-2 text-right font-semibold tabular-nums text-xs">
                      {nonBilling ? (
                        <div className="flex flex-col items-end leading-tight">
                          <span className="line-through text-slate-400 text-xs">{formatCurrency(order.total_charged)}</span>
                          <span className={order.waived ? 'text-red-500' : 'text-slate-500'}>{formatCurrency(0)}</span>
                        </div>
                      ) : (
                        <span className="text-slate-900">{formatCurrency(order.total_charged)}</span>
                      )}
                    </td>

                    {/* Tags */}
                    <td className="px-2 py-2">
                      {(order.tags || []).length > 0
                        ? (order.tags || []).map(tag => <TagBadge key={tag} name={tag} />)
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>

                    {/* Case pick qty — inline editable */}
                    {showCasePick && (
                      <td className="px-1 py-2 text-right">
                        <CasePickInput
                          orderId={order.id}
                          initialQty={order.has_case_pick ? order.case_pick_qty : 0}
                          initialSplitItems={order.split_regular_items || 0}
                          onChange={onCasePickChange}
                        />
                      </td>
                    )}

                    {/* Insert type dropdown */}
                    {showInsertType && (
                      <td className="px-1 py-2">
                        <InsertTypeSelect
                          orderId={order.id}
                          hasCustomInsert={order.has_custom_insert}
                          noInsert={order.no_insert}
                          insertOverridden={order.insert_overridden}
                          insertFee={order.insert_fee}
                          onChange={onInsertTypeChange}
                        />
                      </td>
                    )}

                    {/* Waive toggle */}
                    {showWaive && (
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={e => {
                            e.stopPropagation()
                            if (!order.waived) {
                              if (!window.confirm(`Waive all fees for order ${order.order_number}?\n\nThis will zero pick, pack, label, insert and shipping charges. The carrier cost is preserved for internal records.\n\nYou can undo this by clicking the icon again.`)) return
                            }
                            onWaive(order.id, order.waived)
                          }}
                          title={order.waived ? 'Fees waived — click to restore pricing' : 'Waive all fees for this order (packing error)'}
                          className={`p-1 rounded transition-colors ${order.waived ? 'text-red-500 hover:text-red-700' : 'text-slate-200 hover:text-slate-400'}`}
                        >
                          <Ban size={14} />
                        </button>
                      </td>
                    )}
                    {showEdit && (
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={e => { e.stopPropagation(); onEditOrder(order) }}
                          title="Edit this order's fees"
                          className="p-1 rounded text-slate-300 hover:text-blue-600 transition-colors"
                        >
                          <Edit2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })
            }
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
        {/* Left: count + page size selector */}
        <div className="flex items-center gap-3">
          <p className="text-xs text-slate-500">
            {total === 0
              ? 'No orders'
              : `Showing ${((page - 1) * pageSize) + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()} orders`}
          </p>
          {onPageSizeChange && (
            <select
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              {[20, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n} per page</option>
              ))}
            </select>
          )}
        </div>

        {/* Right: page buttons — always visible */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
          </button>

          {(() => {
            if (totalPages <= 1) {
              return (
                <button className="w-7 h-7 rounded text-xs font-medium bg-blue-600 text-white">1</button>
              )
            }
            const delta = 2
            const range = []
            for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
              range.push(i)
            }
            const pages = [1]
            if (range.length > 0 && range[0] > 2) pages.push('…left')
            pages.push(...range)
            if (range.length > 0 && range[range.length - 1] < totalPages - 1) pages.push('…right')
            pages.push(totalPages)

            return pages.map((p) =>
              typeof p === 'string' ? (
                <span key={p} className="text-slate-400 text-xs px-1">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={[
                    'w-7 h-7 rounded text-xs font-medium transition-colors',
                    p === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100',
                  ].join(' ')}
                >
                  {p}
                </button>
              )
            )
          })()}

          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Courier cost override modal — opens from clicking a carrier_cost cell */}
      {costOverrideOrderId && (
        <CourierCostModal
          orderId={costOverrideOrderId}
          onClose={() => setCostOverrideOrderId(null)}
        />
      )}

      {/* Tracking timeline drawer — opens from clicking the Tracking column */}
      {drawerOrder && (
        <OrderDetailDrawer
          row={{
            ...drawerOrder,
            order_id: drawerOrder.id,
            days_in_transit: daysInTransit(drawerOrder.shipped_date),
          }}
          onClose={() => setDrawerOrder(null)}
          getEvents={getTrackingEvents}
          full
          canRefresh={false}  /* the order-table drawer never shows Refresh (Tracking page keeps it) */
        />
      )}
    </div>
  )
}
